import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { waLink } from "../../lib/site";

const links = [
  { label: "Home", href: "#home" },
  { label: "Our Vehicles", href: "#vehicles" },
  { label: "Rental Requirements", href: "#requirements" },
  { label: "Contact Us", href: "#contact" },
];

export default function Navbar({ hasOffer = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed left-0 z-50 w-full transition-all duration-300 ${hasOffer ? "top-10" : "top-0"} ${
          scrolled ? "bg-ink/70 backdrop-blur-xl saturate-150 border-b border-white/10" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
          <a href="#home" data-testid="nav-logo" className="flex items-center gap-2">
            <span className="font-heading text-lg font-black uppercase leading-none tracking-tight text-white md:text-xl">
              Rent A Car<span className="text-volt">.</span>Kohima
            </span>
          </a>

          <nav className="hidden items-center gap-9 lg:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.split(" ")[0].toLowerCase()}`}
                className="text-xs font-semibold uppercase tracking-[0.15em] text-white/70 transition-colors hover:text-volt"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-whatsapp-cta"
              className="hidden items-center gap-2 bg-volt px-5 py-3 text-xs font-bold uppercase tracking-wider text-ink transition-colors hover:bg-volt-hover sm:inline-flex"
            >
              <MessageCircle size={15} /> WhatsApp Us
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              className="text-white lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-heading text-lg font-black uppercase text-white">
                Menu
              </span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={28} className="text-white" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                  className="font-heading text-4xl font-black uppercase tracking-tight text-white"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="grid grid-cols-2 gap-3 p-6">
              <a href={waLink()} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-volt px-4 py-4 text-sm font-bold uppercase text-ink">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a href="tel:+918787889275" className="flex items-center justify-center gap-2 border border-white/25 px-4 py-4 text-sm font-bold uppercase text-white">
                <Phone size={16} /> Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
